import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {AdyenCheckout} from '@adyen/adyen-web'
import {usePaymentMethodsForBasket} from '@salesforce/commerce-sdk-react'
import {useQuery} from '@tanstack/react-query'
import {ADYEN_CONFIGURATION, CHECKOUT_ORDER_ID, CHECKOUT_TOKEN} from '../constants'
import logger from '../utils/logger-instance'
import {rebuildPathWithParams} from '../utils/url'
import useNavigation from './use-navigation'
import {useCurrentBasket} from './use-current-basket'
import useEffectEvent from './use-effect-event'
import {useToast} from './use-toast'
import {useStokkeOcapiFailOrderMutation} from './useStokkeHelpers'
import useMultiSite from './use-multi-site'
import {FAIL_ORDER_ERROR_MESSAGE, CART_HREF} from '../constants'

/**
 * @param {boolean} [enabled] - optional flag to enable/disable AdyenCheckout
 * @param {String} [requiredMethod] - required Adyen method name that would be used to check if the current customer can use this payment method
 * @returns {{checkout: any, config: any}}
 *
 * ref: https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&integration=Drop-in&version=6.5.1&tab=nodejs_3_4#configure
 */
const useAdyenCheckout = ({enabled, requiredMethod}) => {
    const {formatMessage} = useIntl()
    const {site, locale} = useMultiSite()
    const navigate = useNavigation()
    const toast = useToast()

    const failOrderMutation = useStokkeOcapiFailOrderMutation()

    const {data: basket} = useCurrentBasket()
    const orderTotal = basket?.orderTotal || basket?.productTotal
    const currency = basket?.currency
    const location = rebuildPathWithParams(
        `${window.location.origin}/${site.id}/${locale.id.toLowerCase()}/appcheckout/confirmation`,
        {
            token: basket?.basketId
        }
    )
    const {data: paymentMethods} = usePaymentMethodsForBasket(
        {
            parameters: {
                basketId: basket?.basketId,
                c_returnUrl: location,
                // not used by the api but we use it to
                // trigger a new request and fetch new session
                // when the order total or currency changes
                c_amount: orderTotal,
                c_currency: currency
            }
        },
        {
            enabled: !!basket?.basketId && enabled !== false,
            // cache the payment methods for 15 min as they only depend on the basket amount and currency
            // the session is valid for 2 hour so it should be fine
            // without this on slow connections (3g emulated) this is causing infinite reloading of the session,
            // because by the time the adyen component is rendered the session is already stale and the request is triggered again
            // creating a new session and the cycle repeats
            staleTime: 15 * 60 * 1000 // 15 min
        }
    )

    // parse the adyen session from the server
    const cAdyenSession = paymentMethods?.applicablePaymentMethods?.[0]?.c_adyenSession
    const adyenSessionFromServer = useMemo(() => {
        // check if payment is allowed for the current customer (methods depend on country, customer and customer groups)
        if (!cAdyenSession) {
            return null
        }
        try {
            const result = JSON.parse(cAdyenSession)
            if (!result?.paymentMethodsConfiguration?.[requiredMethod]) {
                return null
            }
            return result
        } catch (e) {
            logger.warn('Failure to parse c_adyenSession', e)
        }
    }, [cAdyenSession, requiredMethod])

    // handle the adyen payment result
    const onAdyenPaymentResult = useEffectEvent((result) => {
        const merchantReference = window.localStorage?.getItem(CHECKOUT_TOKEN)
        const orderId = window.localStorage?.getItem(CHECKOUT_ORDER_ID)
        logger.warn('Adyen express finalisation', result)
        if (
            result.resultCode === 'Error' ||
            result.resultCode === 'Cancelled' ||
            result.resultCode === 'Refused' ||
            // message: "ApplePay token amount-mismatch"
            (result.error && (result?.cause?.status == 422 || result?.cause?.status == 500))
        ) {
            failOrderMutation.mutateAsync(
                {
                    orderToken: merchantReference,
                    errorReason: `payment completed error reason: ${result.resultCode}`
                },
                {
                    onSettled: () => {
                        // cart page reloads in order to start from scratch with newly created basket after order fail
                        navigate(CART_HREF, 'push', {
                            cartError: formatMessage(FAIL_ORDER_ERROR_MESSAGE)
                        })
                    }
                }
            )
        } else if (result?.error?.applePayError?.message) {
            // in case there is an error that is triggered before order creation just render the message
            // so the customer would know what the detailed problem is
            toast({
                title: result?.error?.applePayError?.message,
                status: 'error'
            })
        } else {
            window.localStorage.setItem(ADYEN_CONFIGURATION, cAdyenSession)
            navigate(
                // pass both merchantReference and orderId to the confirmation page
                // as on PRD the apple pay express payment cannot load the order using the token
                `/appcheckout/confirmation?token=${merchantReference}&orderId=${orderId}`
            )
        }
    })

    // prepare complete adyen configuration
    const adyenConfig = useMemo(() => {
        if (!adyenSessionFromServer) {
            return null
        }
        return {
            // avoid modifying the original
            ...adyenSessionFromServer,
            amount: orderTotal,
            instantPaymentTypes: ['applepay'],
            onPaymentCompleted: onAdyenPaymentResult,
            onPaymentFailed: onAdyenPaymentResult,
            onError: (errorInfo) => {
                logger.error('Adyen transaction error', errorInfo)
            }
        }
    }, [adyenSessionFromServer, orderTotal, onAdyenPaymentResult])

    // use query to reuse and cache the AdyenCheckout instance as it takes some time to create
    // at the same time given the same config it can be reused
    return (
        useQuery({
            queryKey: ['useAdyenCheckout', adyenConfig],
            queryFn: async ({queryKey: [, adyenConfig]}) => {
                return {
                    checkout: await AdyenCheckout(adyenConfig),
                    config: adyenConfig
                }
            },
            enabled: !!adyenConfig && enabled !== false
        }).data || {}
    )
}

export default useAdyenCheckout
